import { destinoDe, tituloDe } from "./rutaDelMenu";

/**
 * Buscar una pantalla por su nombre, sin recorrer el árbol.
 *
 * Son veintitantos destinos repartidos en tres submenús, y desde que la barra
 * está oculta por omisión hay que abrirla y desplegar el submenú correcto para
 * llegar a cualquiera. Escribir tres letras es más corto que acordarse de si
 * «Aperturas de caja» vive bajo «Cajas» o bajo «Gestión» --que es justo la
 * duda que provoca este menú, porque hay un submenú «Cajas» y una pantalla
 * «Cajas» dentro--.
 */

/**
 * Para comparar: sin mayúsculas y sin acentos.
 *
 * Sin esto, «campanas» no encuentra «Campañas» y «facturacion» no encuentra
 * «Facturación» --y nadie escribe los acentos cuando busca deprisa--. La ñ se
 * descompone igual que las vocales acentuadas, así que «campanas» y «campañas»
 * llevan las dos al mismo sitio.
 */
export const paraComparar = (texto) =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

/**
 * Las pantallas cuyo nombre --o el de su submenú-- contiene lo escrito.
 *
 * Devuelve una lista plana y no un árbol podado: quien busca quiere ver el
 * destino, no navegar hasta él. El nombre del submenú viaja aparte, como
 * contexto, porque «Cajas» y «Cajas › Cajas» no son lo mismo.
 *
 * @param {Array} items Los ítems del menú, como los devuelve `getMenuItems`.
 * @param {string} texto Lo que se escribió.
 * @returns {Array<{key: string, titulo: string, destino: string, padre: string|null}>}
 */
export const buscarEnElMenu = (items = [], texto = "") => {
  const buscado = paraComparar(texto);
  if (!buscado) return [];

  const encontrados = [];

  const recorrer = (lista, padre) => {
    (lista || []).forEach((item) => {
      if (!item) return;
      const titulo = tituloDe(item);
      const destino = destinoDe(item);

      // Un submenú no es un destino: no se puede ir a «Gestión». Pero su
      // nombre sí cuenta para encontrar lo que hay dentro, así que escribir
      // «gestion» saca todas las pantallas que cuelgan de él.
      if (item.children) {
        recorrer(item.children, titulo);
        return;
      }

      if (!destino || !titulo) return;
      const coincide =
        paraComparar(titulo).includes(buscado) || paraComparar(padre).includes(buscado);
      if (coincide) encontrados.push({ key: String(item.key), titulo, destino, padre: padre ?? null });
    });
  };

  recorrer(items, null);

  // Lo que empieza por lo escrito va primero: quien teclea «fac» busca
  // «Facturas» antes que «Formas de pago», aunque las dos contengan «fa».
  return encontrados.sort((a, b) => {
    const empiezaA = paraComparar(a.titulo).startsWith(buscado) ? 0 : 1;
    const empiezaB = paraComparar(b.titulo).startsWith(buscado) ? 0 : 1;
    return empiezaA - empiezaB || a.titulo.localeCompare(b.titulo, "es");
  });
};
